import React, { useCallback, useEffect, useState } from "react";
import { FaChevronDown, FaChevronRight, FaEdit, FaPlus, FaRegTrashAlt } from "react-icons/fa";
import { Check, X } from "lucide-react";
import HeaderButtons from "components/Button/HeaderButtons";
import { fieldCls } from "components/InlineTable";
import Loader from "components/Loader";
import { useToaster } from "contexts/toasterContext";
import { getApiErrorMessage } from "util/errorMessage";
import feedbackManager from "services/Feedback/feedbackManager";
import {
  KraArea,
  KraObjective,
  MAX_AREA_NAME_LENGTH,
  MAX_OBJECTIVE_NAME_LENGTH,
} from "services/Feedback/feedbackInterface";

/**
 * KRA areas and objectives — the goal-sheet dropdowns.
 *
 * NOTE: a new component rather than `GenericConfigDetail`, which renders a FLAT
 * name list. This config is two levels deep (an area owns its objectives) and
 * each level carries an `isActive` toggle as well as a name, so reusing the
 * generic editor would mean rewriting it for one caller. The visual language —
 * inline edit, default badge, delete confirmation, toasts — is kept identical
 * to the other AdminConfig screens.
 *
 * Seeded rows (`isDefault`) can be reordered and deactivated but never renamed
 * or deleted, matching both the server rules and the convention already used
 * for default values elsewhere in Admin Config.
 */

type EditTarget =
  | { level: "area"; id: number }
  | { level: "objective"; id: number }
  | null;

const INPUT_CLS = "text-sm";

const KraConfig: React.FC = () => {
  const { addToast } = useToaster();

  const [areas, setAreas] = useState<KraArea[]>([]);
  const [loading, setLoading] = useState(true);
  const [apiError, setApiError] = useState("");
  const [expanded, setExpanded] = useState<Set<number>>(new Set());

  const [editTarget, setEditTarget] = useState<EditTarget>(null);
  const [editValue, setEditValue] = useState("");

  const [newAreaName, setNewAreaName] = useState("");
  const [addingArea, setAddingArea] = useState(false);
  /** Area id currently showing its "add objective" input. */
  const [objectiveParent, setObjectiveParent] = useState<number | null>(null);
  const [newObjectiveName, setNewObjectiveName] = useState("");

  /** Any in-flight write, so every control can be disabled together. */
  const [busy, setBusy] = useState(false);

  // ── Load ──────────────────────────────────────────────────────────────────

  const loadAreas = useCallback(async () => {
    setLoading(true);
    setApiError("");
    try {
      // `true` so deactivated rows stay visible and manageable here.
      const response = await feedbackManager.getKraAreas(true);
      setAreas(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      // A load failure keeps the inline banner: there is no action behind it
      // and the message must persist while the screen is unusable.
      setApiError(getApiErrorMessage(error, "Failed to load KRA areas."));
      setAreas([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadAreas();
  }, [loadAreas]);

  // ── Helpers ───────────────────────────────────────────────────────────────

  const toggleExpanded = (areaId: number) => {
    setExpanded((previous) => {
      const next = new Set(previous);
      if (next.has(areaId)) next.delete(areaId);
      else next.add(areaId);
      return next;
    });
  };

  const startEdit = (target: Exclude<EditTarget, null>, currentName: string, isDefault: boolean) => {
    // Seeded rows can't be renamed — guarded here as well as on the button, so
    // no other path into edit mode can bypass it.
    if (busy || isDefault) return;
    setEditTarget(target);
    setEditValue(currentName);
  };

  const cancelEdit = () => {
    setEditTarget(null);
    setEditValue("");
  };

  const isEditing = (level: "area" | "objective", id: number) =>
    editTarget?.level === level && editTarget.id === id;

  /** Runs a write, then refreshes and toasts. Keeps every handler identical. */
  const runWrite = async (
    action: () => Promise<unknown>,
    successMessage: string,
    failureMessage: string,
  ): Promise<boolean> => {
    setBusy(true);
    try {
      await action();
      addToast(successMessage, "success");
      await loadAreas();
      return true;
    } catch (error) {
      addToast(getApiErrorMessage(error, failureMessage), "error");
      return false;
    } finally {
      setBusy(false);
    }
  };

  // ── Area actions ──────────────────────────────────────────────────────────

  const handleAddArea = async () => {
    const name = newAreaName.trim();
    if (!name) return;

    const ok = await runWrite(
      () => feedbackManager.createKraArea({ name }),
      `KRA area "${name}" added successfully`,
      "Failed to add the KRA area",
    );
    if (ok) {
      setNewAreaName("");
      setAddingArea(false);
    }
  };

  const handleRenameArea = async (area: KraArea) => {
    const name = editValue.trim();
    if (!name || name === area.name) {
      cancelEdit();
      return;
    }

    const ok = await runWrite(
      () => feedbackManager.updateKraArea(area.id, { name }),
      "KRA area updated successfully",
      "Failed to update the KRA area",
    );
    if (ok) cancelEdit();
  };

  const handleToggleArea = (area: KraArea) =>
    runWrite(
      () => feedbackManager.updateKraArea(area.id, { isActive: !area.isActive }),
      `"${area.name}" ${area.isActive ? "deactivated" : "activated"}`,
      "Failed to change the KRA area",
    );

  const handleDeleteArea = (area: KraArea) =>
    runWrite(
      () => feedbackManager.deleteKraArea(area.id),
      `KRA area "${area.name}" deleted successfully`,
      "Failed to delete the KRA area",
    );

  // ── Objective actions ─────────────────────────────────────────────────────

  const handleAddObjective = async (areaId: number) => {
    const name = newObjectiveName.trim();
    if (!name) return;

    const ok = await runWrite(
      () => feedbackManager.createKraObjective(areaId, { name }),
      `Objective "${name}" added successfully`,
      "Failed to add the objective",
    );
    if (ok) {
      setNewObjectiveName("");
      setObjectiveParent(null);
    }
  };

  const handleRenameObjective = async (objective: KraObjective) => {
    const name = editValue.trim();
    if (!name || name === objective.name) {
      cancelEdit();
      return;
    }

    const ok = await runWrite(
      () => feedbackManager.updateKraObjective(objective.id, { name }),
      "Objective updated successfully",
      "Failed to update the objective",
    );
    if (ok) cancelEdit();
  };

  const handleToggleObjective = (objective: KraObjective) =>
    runWrite(
      () => feedbackManager.updateKraObjective(objective.id, { isActive: !objective.isActive }),
      `"${objective.name}" ${objective.isActive ? "deactivated" : "activated"}`,
      "Failed to change the objective",
    );

  const handleDeleteObjective = (objective: KraObjective) =>
    runWrite(
      () => feedbackManager.deleteKraObjective(objective.id),
      `Objective "${objective.name}" deleted successfully`,
      "Failed to delete the objective",
    );

  // ── Shared row pieces ─────────────────────────────────────────────────────

  const DefaultBadge = (
    <span
      title="Seeded system value — can't be renamed or deleted"
      className="rounded bg-slate-100 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-slate-500 dark:bg-slate-700 dark:text-slate-300"
    >
      Default
    </span>
  );

  const InactiveBadge = (
    <span className="rounded bg-gray-100 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-gray-500 dark:bg-gray-700 dark:text-gray-400">
      Inactive
    </span>
  );

  /** Save / cancel pair shown while a name is being edited inline. */
  const renderEditControls = (onSave: () => void) => (
    <div className="flex items-center gap-1">
      <button
        type="button"
        onClick={onSave}
        disabled={busy}
        title="Save"
        aria-label="Save name"
        className="rounded p-1.5 text-emerald-600 transition-colors hover:bg-emerald-50 disabled:opacity-50 dark:hover:bg-emerald-900/20"
      >
        <Check size={14} />
      </button>
      <button
        type="button"
        onClick={cancelEdit}
        disabled={busy}
        title="Cancel"
        aria-label="Cancel editing"
        className="rounded p-1.5 text-gray-400 transition-colors hover:bg-gray-100 disabled:opacity-50 dark:hover:bg-gray-700"
      >
        <X size={14} />
      </button>
    </div>
  );

  // ── Render ────────────────────────────────────────────────────────────────

  if (loading) {
    return (
      <div className="flex min-h-[200px] items-center justify-center">
        <Loader />
      </div>
    );
  }

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <h2 className="text-base font-semibold text-gray-900 dark:text-white">
            KRA areas &amp; objectives
          </h2>
          <p className="text-xs text-gray-500 dark:text-gray-400">
            The dropdowns employees pick from on their goal sheet. Deactivating hides a value from
            new sheets; saved feedback keeps it.
          </p>
        </div>
        <HeaderButtons
          type="button"
          variant="primary"
          size="small"
          icon={<FaPlus size={11} />}
          disabled={busy || addingArea}
          onClick={() => setAddingArea(true)}
        >
          Add area
        </HeaderButtons>
      </div>

      {apiError && (
        <div className="rounded-lg bg-red-50 px-3 py-2 text-sm font-medium text-error dark:bg-red-900/20">
          {apiError}
        </div>
      )}

      {addingArea && (
        <div className="flex items-center gap-2 rounded-lg border border-gray-200 p-2.5 dark:border-gray-700">
          <input
            autoFocus
            type="text"
            value={newAreaName}
            maxLength={MAX_AREA_NAME_LENGTH}
            placeholder="New KRA area name"
            disabled={busy}
            onChange={(e) => setNewAreaName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") void handleAddArea();
              if (e.key === "Escape") setAddingArea(false);
            }}
            className={`${fieldCls(false)} ${INPUT_CLS}`}
          />
          <HeaderButtons
            type="button"
            variant="primary"
            size="small"
            onClick={handleAddArea}
            loading={busy}
            disabled={busy || newAreaName.trim() === ""}
          >
            Add
          </HeaderButtons>
          <HeaderButtons
            type="button"
            variant="tertiary"
            size="small"
            onClick={() => {
              setAddingArea(false);
              setNewAreaName("");
            }}
            disabled={busy}
          >
            Cancel
          </HeaderButtons>
        </div>
      )}

      <div className="flex min-h-0 flex-1 flex-col gap-2 overflow-y-auto">
        {areas.length === 0 && !apiError && (
          <div className="rounded-xl border border-dashed border-gray-200 px-6 py-10 text-center dark:border-gray-700">
            <p className="text-sm font-medium text-gray-900 dark:text-white">No KRA areas yet</p>
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
              Add one to give employees something to pick on their goal sheet.
            </p>
          </div>
        )}

        {areas.map((area) => {
          const isOpen = expanded.has(area.id);
          const editingThisArea = isEditing("area", area.id);

          return (
            <div
              key={area.id}
              className={`rounded-lg border dark:border-gray-700 ${
                area.isActive ? "border-gray-200" : "border-gray-200 bg-gray-50 dark:bg-gray-800/60"
              }`}
            >
              {/* ── Area row ────────────────────────────────────────────── */}
              <div className="flex flex-wrap items-center gap-2 px-3 py-2.5">
                <button
                  type="button"
                  onClick={() => toggleExpanded(area.id)}
                  aria-expanded={isOpen}
                  aria-label={isOpen ? "Collapse objectives" : "Expand objectives"}
                  className="rounded p-1 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600 dark:hover:bg-gray-700"
                >
                  {isOpen ? <FaChevronDown size={11} /> : <FaChevronRight size={11} />}
                </button>

                {editingThisArea ? (
                  <>
                    <input
                      autoFocus
                      type="text"
                      value={editValue}
                      maxLength={MAX_AREA_NAME_LENGTH}
                      disabled={busy}
                      onChange={(e) => setEditValue(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") void handleRenameArea(area);
                        if (e.key === "Escape") cancelEdit();
                      }}
                      className={`${fieldCls(false)} ${INPUT_CLS} flex-1 min-w-[160px]`}
                    />
                    {renderEditControls(() => void handleRenameArea(area))}
                  </>
                ) : (
                  <>
                    <span className="flex-1 truncate text-sm font-medium text-gray-900 dark:text-white">
                      {area.name}
                    </span>
                    {area.isDefault && DefaultBadge}
                    {!area.isActive && InactiveBadge}
                    <span className="text-xs text-gray-400 dark:text-gray-500">
                      {area.objectives.length} objective
                      {area.objectives.length === 1 ? "" : "s"}
                    </span>

                    <div className="flex items-center gap-1">
                      <button
                        type="button"
                        onClick={() => handleToggleArea(area)}
                        disabled={busy}
                        title={area.isActive ? "Deactivate" : "Activate"}
                        className="rounded px-2 py-1 text-xs font-medium text-gray-500 transition-colors hover:bg-gray-100 disabled:opacity-50 dark:text-gray-400 dark:hover:bg-gray-700"
                      >
                        {area.isActive ? "Deactivate" : "Activate"}
                      </button>
                      <button
                        type="button"
                        onClick={() =>
                          startEdit({ level: "area", id: area.id }, area.name, area.isDefault)
                        }
                        disabled={busy || area.isDefault}
                        title={area.isDefault ? "Default areas can't be renamed" : "Rename"}
                        aria-label="Rename area"
                        className="rounded p-1.5 text-gray-400 transition-colors hover:bg-gray-100 hover:text-primary disabled:cursor-not-allowed disabled:opacity-40 dark:hover:bg-gray-700"
                      >
                        <FaEdit size={12} />
                      </button>
                      <button
                        type="button"
                        onClick={() => handleDeleteArea(area)}
                        disabled={busy || area.isDefault}
                        title={
                          area.isDefault ? "Default areas can't be deleted" : "Delete this area"
                        }
                        aria-label="Delete area"
                        className="rounded p-1.5 text-gray-400 transition-colors hover:bg-red-50 hover:text-error disabled:cursor-not-allowed disabled:opacity-40 dark:hover:bg-red-900/20"
                      >
                        <FaRegTrashAlt size={12} />
                      </button>
                    </div>
                  </>
                )}
              </div>

              {/* ── Objectives ──────────────────────────────────────────── */}
              {isOpen && (
                <div className="border-t border-gray-100 px-3 py-2 dark:border-gray-700">
                  {area.objectives.length === 0 && objectiveParent !== area.id && (
                    <p className="py-1.5 text-xs text-gray-400 dark:text-gray-500">
                      No objectives in this area yet.
                    </p>
                  )}

                  {area.objectives.map((objective) => {
                    const editingThis = isEditing("objective", objective.id);

                    return (
                      <div
                        key={objective.id}
                        className="flex flex-wrap items-center gap-2 border-b border-gray-50 py-1.5 last:border-b-0 dark:border-gray-700/50"
                      >
                        {editingThis ? (
                          <>
                            <input
                              autoFocus
                              type="text"
                              value={editValue}
                              maxLength={MAX_OBJECTIVE_NAME_LENGTH}
                              disabled={busy}
                              onChange={(e) => setEditValue(e.target.value)}
                              onKeyDown={(e) => {
                                if (e.key === "Enter") void handleRenameObjective(objective);
                                if (e.key === "Escape") cancelEdit();
                              }}
                              className={`${fieldCls(false)} ${INPUT_CLS} flex-1 min-w-[160px]`}
                            />
                            {renderEditControls(() => void handleRenameObjective(objective))}
                          </>
                        ) : (
                          <>
                            <span
                              className={`flex-1 truncate text-sm ${
                                objective.isActive
                                  ? "text-gray-700 dark:text-gray-200"
                                  : "text-gray-400 line-through dark:text-gray-500"
                              }`}
                            >
                              {objective.name}
                            </span>
                            {objective.isDefault && DefaultBadge}
                            {!objective.isActive && InactiveBadge}

                            <div className="flex items-center gap-1">
                              <button
                                type="button"
                                onClick={() => handleToggleObjective(objective)}
                                disabled={busy}
                                className="rounded px-2 py-1 text-xs font-medium text-gray-500 transition-colors hover:bg-gray-100 disabled:opacity-50 dark:text-gray-400 dark:hover:bg-gray-700"
                              >
                                {objective.isActive ? "Deactivate" : "Activate"}
                              </button>
                              <button
                                type="button"
                                onClick={() =>
                                  startEdit(
                                    { level: "objective", id: objective.id },
                                    objective.name,
                                    objective.isDefault,
                                  )
                                }
                                disabled={busy || objective.isDefault}
                                title={
                                  objective.isDefault
                                    ? "Default objectives can't be renamed"
                                    : "Rename"
                                }
                                aria-label="Rename objective"
                                className="rounded p-1.5 text-gray-400 transition-colors hover:bg-gray-100 hover:text-primary disabled:cursor-not-allowed disabled:opacity-40 dark:hover:bg-gray-700"
                              >
                                <FaEdit size={11} />
                              </button>
                              <button
                                type="button"
                                onClick={() => handleDeleteObjective(objective)}
                                disabled={busy || objective.isDefault}
                                title={
                                  objective.isDefault
                                    ? "Default objectives can't be deleted"
                                    : "Delete this objective"
                                }
                                aria-label="Delete objective"
                                className="rounded p-1.5 text-gray-400 transition-colors hover:bg-red-50 hover:text-error disabled:cursor-not-allowed disabled:opacity-40 dark:hover:bg-red-900/20"
                              >
                                <FaRegTrashAlt size={11} />
                              </button>
                            </div>
                          </>
                        )}
                      </div>
                    );
                  })}

                  {objectiveParent === area.id ? (
                    <div className="flex items-center gap-2 pt-2">
                      <input
                        autoFocus
                        type="text"
                        value={newObjectiveName}
                        maxLength={MAX_OBJECTIVE_NAME_LENGTH}
                        placeholder="New objective name"
                        disabled={busy}
                        onChange={(e) => setNewObjectiveName(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") void handleAddObjective(area.id);
                          if (e.key === "Escape") setObjectiveParent(null);
                        }}
                        className={`${fieldCls(false)} ${INPUT_CLS}`}
                      />
                      <HeaderButtons
                        type="button"
                        variant="primary"
                        size="small"
                        onClick={() => handleAddObjective(area.id)}
                        loading={busy}
                        disabled={busy || newObjectiveName.trim() === ""}
                      >
                        Add
                      </HeaderButtons>
                      <HeaderButtons
                        type="button"
                        variant="tertiary"
                        size="small"
                        onClick={() => {
                          setObjectiveParent(null);
                          setNewObjectiveName("");
                        }}
                        disabled={busy}
                      >
                        Cancel
                      </HeaderButtons>
                    </div>
                  ) : (
                    <button
                      type="button"
                      onClick={() => {
                        setObjectiveParent(area.id);
                        setNewObjectiveName("");
                      }}
                      disabled={busy}
                      className="mt-1.5 inline-flex items-center gap-1.5 text-xs font-medium text-primary transition-opacity hover:opacity-80 disabled:opacity-50"
                    >
                      <FaPlus size={9} /> Add objective
                    </button>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default KraConfig;
